import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import TopBar from './components/TopBar';
import OpenDialog from './components/OpenDialog';
import LoadFromInventoryDialog from './components/LoadFromInventoryDialog';
import ToolsPanel from './components/ToolsPanel';
import BorderPanel from './components/BorderPanel';
import ShapesPanel from './components/ShapesPanel';
import CanvasStage from './components/CanvasStage';
import LayersPanel from './components/LayersPanel';
import PropertiesPanel from './components/PropertiesPanel';
import BlendPanel from './components/BlendPanel';
import TexturesPanel from './components/TexturesPanel';
import PageSettingsDialog from './components/PageSettingsDialog';
import FiltersPanel from './components/FiltersPanel';
import AlignmentPanel from './components/AlignmentPanel';
import { Paper, Box, Tabs, Tab, Menu, MenuItem, Divider, IconButton, Typography, Button, Popover, useTheme } from '@mui/material';
import { usePhotoAddsStore } from './usePhotoAddsStoreBridge';
import { addText, addRect, addCircle, addLine, downloadImage, saveProject, saveProjectJsonToLocation, updateProjectJsonAsset, setCanvasBackgroundFromUrl, addImageLayerFromUrl, saveTemplateJsonToLocation, loadTemplateAssetJson, applyTemplateJson, populateTemplateWithInventory, addSvgShape, updateTemplateJsonAsset } from './services/canvasService';
import { filters as fabricFilters, PencilBrush, Rect } from 'fabric';
import { ZoomIn, ZoomOut, Undo as UndoIcon, Redo as RedoIcon, PanToolAlt as PanToolIcon, FitScreen as FitScreenIcon, CenterFocusWeak as ActualSizeIcon } from '@mui/icons-material';
import { useLocation } from '../../../../contexts/LocationContext';
import api from '../../../../utils/apiConfig';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ height: '100%' }}>{value === index && <Box sx={{ p: 0, height: '100%', overflowY: 'auto' }}>{children}</Box>}</div>
);

const PhotoAdsIndex = () => {
  const theme = useTheme();
  const {
    activeTab, setActiveTab,
    selectedTool, setSelectedTool,
    brushSize, strokeColor, fillColor, strokeWidth,
    fontSize, fontFamily,
    brightness, contrast, saturation,
    selectedFilter, setSelectedFilter,
    canvas, setCanvas,
    layers, setLayers, selectedLayer, setSelectedLayer,
    updateLayersFromCanvas,
  } = usePhotoAddsStore();
  const canvasRefExternal = useRef(null);
  const isLayersDragging = useRef(false);
  const stableIdSeq = useRef(1);
  const { selectedLocation } = useLocation();
  const [currentAsset, setCurrentAsset] = useState(null); // { id, name, mimeType }
  const [borderOptions, setBorderOptions] = useState({ color: theme.palette.common.white, strokeWidth: 6, inset: 0, dashed: false, radius: 0, locked: true });
  const [borderAnchorEl, setBorderAnchorEl] = useState(null);
  const openBorderPopover = (e) => setBorderAnchorEl(e.currentTarget);
  const closeBorderPopover = () => setBorderAnchorEl(null);

  // Right panel resizer setup
  const containerRef = useRef(null);
  const [rightWidth, setRightWidth] = useState(300);
  const [isResizing, setIsResizing] = useState(false);
  const minRight = 220;
  const maxRight = 520;

  // Zoom state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [panMode, setPanMode] = useState(false);
  const outerViewportRef = useRef(null); // scrollable container around the canvas
  const transformRef = useRef(null); // inner host that receives CSS transform

  // History stacks for undo/redo
  const isRestoringRef = useRef(false);
  const historyPastRef = useRef([]); // array of JSON snapshots
  const historyFutureRef = useRef([]);

  const onResizerMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // ----- Viewport transform (translate + scale applied to wrapper) -----
  const applyViewportTransform = useCallback((z = zoom, p = pan) => {
    if (!canvas) return;
    const clamped = Math.max(0.1, Math.min(4, z));
    try {
      // Ensure internal zoom stays at 1 so exports and coordinates remain stable
      if (typeof canvas.setZoom === 'function') canvas.setZoom(1);
      const host = canvas.wrapperEl || transformRef.current || canvas.upperCanvasEl?.parentElement || canvas.lowerCanvasEl?.parentElement;
      if (host && host.style) {
        host.style.transformOrigin = 'center center';
        host.style.transform = `translate(${p.x}px, ${p.y}px) scale(${clamped})`;
      }
    } catch (_) {}
    // Update Fabric cached element offsets and repaint
    try { canvas.calcOffset && canvas.calcOffset(); } catch (_) {}
    canvas.requestRenderAll();
  }, [canvas, zoom, pan]);

  // ----- Zoom helpers -----
  const applyZoom = useCallback((z) => {
    const clamped = Math.max(0.1, Math.min(4, z));
    setZoom(clamped);
    applyViewportTransform(clamped, pan);
  }, [applyViewportTransform, pan]);

  const zoomIn = () => applyZoom(zoom * 1.1);
  const zoomOut = () => applyZoom(zoom / 1.1);

  // ----- Pan interactions (hand tool) -----
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const mouseStartRef = useRef({ x: 0, y: 0 });

  const onPanMouseDown = useCallback((e) => {
    if (!panMode) return;
    isPanningRef.current = true;
    mouseStartRef.current = { x: e.clientX, y: e.clientY };
    panStartRef.current = { ...pan };
    if (canvas) {
      canvas.skipTargetFind = true;
      canvas.selection = false;
    }
  }, [panMode, pan, canvas]);

  const onPanMouseMove = useCallback((e) => {
    if (!isPanningRef.current) return;
    const dx = e.clientX - mouseStartRef.current.x;
    const dy = e.clientY - mouseStartRef.current.y;
    const next = { x: panStartRef.current.x + dx, y: panStartRef.current.y + dy };
    setPan(next);
    applyViewportTransform(zoom, next);
  }, [applyViewportTransform, zoom]);

  const endPan = useCallback(() => {
    if (!isPanningRef.current) return;
    isPanningRef.current = false;
    if (canvas) {
      canvas.skipTargetFind = false;
      canvas.selection = true;
    }
  }, [canvas]);

  const togglePanMode = () => setPanMode(v => !v);

  // Fit to screen: scale to fit viewport and center; resets pan to 0
  const fitToScreen = useCallback(() => {
    if (!canvas || !outerViewportRef.current) return;
    const cw = canvas.getWidth?.() || canvas.width || 1;
    const ch = canvas.getHeight?.() || canvas.height || 1;
    const rect = outerViewportRef.current.getBoundingClientRect?.();
    if (!rect) return;
    const padding = 24; // a bit of breathing space
    const availW = Math.max(1, rect.width - padding * 2);
    const availH = Math.max(1, rect.height - padding * 2);
    const scale = Math.min(availW / cw, availH / ch);
    setPan({ x: 0, y: 0 });
    setZoom(scale);
    applyViewportTransform(scale, { x: 0, y: 0 });
  }, [canvas, applyViewportTransform]);

  const resetZoomActual = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    applyViewportTransform(1, { x: 0, y: 0 });
  }, [applyViewportTransform]);

  // ----- Undo/Redo helpers -----
  const snapshotCanvas = (c) => {
    // preserve some custom props used in layers
    return c.toJSON(['name', '__layerId', '__isPageBorder']);
  };

  const recordHistory = (c = canvas) => {
    if (!c || isRestoringRef.current) return;
    try {
      const snap = snapshotCanvas(c);
      const past = historyPastRef.current;
      const last = past[past.length - 1];
      const snapStr = JSON.stringify(snap);
      const lastStr = last ? JSON.stringify(last) : null;
      if (snapStr !== lastStr) {
        past.push(snap);
        if (past.length > 50) past.shift();
        historyFutureRef.current = [];
      }
    } catch (_) {}
  };

  // Debounced checkpoint to avoid flooding history on drags/typing
  const historyDebounceRef = useRef(null);
  const recordHistoryDebounced = useCallback((c = canvas, delay = 150) => {
    if (historyDebounceRef.current) clearTimeout(historyDebounceRef.current);
    historyDebounceRef.current = setTimeout(() => recordHistory(c), delay);
  }, [canvas]);

  const undo = async () => {
    if (!canvas) return;
    const past = historyPastRef.current;
    if (past.length < 2) return; // current + at least one previous
    const current = past.pop();
    const prev = past[past.length - 1];
    if (!prev) return;
    historyFutureRef.current.push(current);
    isRestoringRef.current = true;
    await new Promise((resolve) => canvas.loadFromJSON(prev, () => { canvas.renderAll(); resolve(); }));
    applyViewportTransform(zoom, pan);
    isRestoringRef.current = false;
    safeUpdateLayersFromCanvas();
  };

  const redo = async () => {
    if (!canvas) return;
    const future = historyFutureRef.current;
    if (future.length === 0) return;
    const next = future.pop();
    if (!next) return;
    historyPastRef.current.push(next);
    isRestoringRef.current = true;
    await new Promise((resolve) => canvas.loadFromJSON(next, () => { canvas.renderAll(); resolve(); }));
    applyViewportTransform(zoom, pan);
    isRestoringRef.current = false;
    safeUpdateLayersFromCanvas();
  };

  // Border helpers
  const findBorderObject = () => {
    if (!canvas) return null;
    const objs = canvas.getObjects();
    return objs.find(o => o && (o.name === 'Page Border' || o.__isPageBorder));
  };

  const addOrUpdateBorder = (opts, { silentCreate = false } = {}) => {
    if (!canvas) return;
    const { color = theme.palette.common.white, strokeWidth = 6, inset = 0, dashed = false, radius = 0, locked = true } = opts || {};
    const x = Math.max(0, inset);
    const y = Math.max(0, inset);
    const w = Math.max(0, (canvas.getWidth?.() || canvas.width) - x * 2);
    const h = Math.max(0, (canvas.getHeight?.() || canvas.height) - y * 2);
    let border = findBorderObject();
    if (!border) {
      // Create a new rect with transparent fill and stroke
      border = new Rect({ left: x, top: y, width: w, height: h, fill: 'transparent', stroke: color, strokeWidth, rx: radius, ry: radius, selectable: !locked, evented: !locked, name: 'Page Border' });
      border.__isPageBorder = true;
      if (dashed) border.set({ strokeDashArray: [8, 6] }); else border.set({ strokeDashArray: null });
      canvas.add(border);
    } else {
      border.set({ left: x, top: y, width: w, height: h, stroke: color, strokeWidth, rx: radius, ry: radius, selectable: !locked, evented: !locked });
      if (dashed) border.set({ strokeDashArray: [8, 6] }); else border.set({ strokeDashArray: null });
    }
    // Ensure border is on top
    if (border.bringToFront) border.bringToFront();
    canvas.requestRenderAll();
    if (!silentCreate) safeUpdateLayersFromCanvas();
  };

  const handleBorderChange = (opts) => setBorderOptions(opts);
  const handleBorderPreset = (preset) => {
    const merged = { ...borderOptions, ...preset };
    setBorderOptions(merged);
    addOrUpdateBorder(merged);
  };
  const handleBorderApply = (opts) => {
    const merged = { ...borderOptions, ...opts };
    setBorderOptions(merged);
    addOrUpdateBorder(merged);
  };

  // Ensure stable IDs per Fabric object to keep dnd-kit from snapping back due to remounts
  const panelLayers = useMemo(() => {
    if (!Array.isArray(layers)) return [];
    return layers.map((l) => {
      const obj = l?.object;
      if (obj && !obj.__layerId) {
        obj.__layerId = l?.id || `L${stableIdSeq.current++}`;
      }
      return { ...l, id: obj?.__layerId || l?.id };
    });
  }, [layers]);

  // Template actions
  const handleSaveAsTemplate = async () => {
    if (!canvas) return;
    const locationId = selectedLocation?.id || selectedLocation?._id;
    if (!locationId) {
      alert('No active location selected. Please select a location first.');
      return;
    }
    try {
      const name = window.prompt('Enter a name for this template');
      if (!name) return;
      const created = await saveTemplateJsonToLocation(canvas, { locationId, name });
      const assetId = created?.data?.id || created?.id || created?.data?.data?.id;
      if (assetId) {
        await uploadTemplateThumbnail(assetId);
      }
      alert('Template saved');
    } catch (e) {
      console.error(e);
      alert('Failed to save template');
    }
  };

  // Add selected SVG shape from ShapesPanel
  const handleAddShape = async (shape) => {
    if (!canvas || !shape?.svg) return;
    try {
      await addSvgShape(canvas, shape.svg, { center: true, fill: fillColor, stroke: strokeColor, strokeWidth, targetWidth: 180 });
      safeUpdateLayersFromCanvas();
    } catch (e) {
      console.error('Failed to add shape', e);
      alert('Failed to add shape');
    }
  };

  const onLoadFromInventory = () => setLoadInvOpen(true);
  const handleLoadInvClose = () => setLoadInvOpen(false);
  const handleCreateAdFromInventory = async (item, template, selectedImages) => {
    if (!canvas || !template?.id) return;
    setCreatingAd(true);
    try {
      const json = await loadTemplateAssetJson(template.id);
      // Resize canvas to template's stored dimensions if provided
      const tw = Number(template.width);
      const th = Number(template.height);
      if (tw > 0 && th > 0) {
        canvas.setWidth(Math.round(tw));
        canvas.setHeight(Math.round(th));
      }
      await applyTemplateJson(canvas, json);
      await populateTemplateWithInventory(canvas, item, selectedImages);
      safeUpdateLayersFromCanvas();
      setLoadInvOpen(false);
      setCurrentAsset(null); // new unsaved project based on template
    } catch (e) {
      console.error('Create Ad failed', e);
      alert(e?.message || 'Failed to create ad from inventory');
    } finally {
      setCreatingAd(false);
    }
  };

  const onMouseMove = useCallback((e) => {
    if (!isResizing || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const containerRight = rect.right;
    const newWidth = Math.min(maxRight, Math.max(minRight, containerRight - e.clientX));
    setRightWidth(newWidth);
  }, [isResizing]);

  const onMouseUp = useCallback(() => {
    if (isResizing) setIsResizing(false);
  }, [isResizing]);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
      return () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };
    }
  }, [isResizing, onMouseMove, onMouseUp]);

  // Apply drawing settings when tool or brush settings change
  useEffect(() => {
    if (!canvas) return;
    if (selectedTool === 'select') {
      canvas.isDrawingMode = false;
      canvas.selection = true;
    } else if (selectedTool === 'brush') {
      canvas.isDrawingMode = true;
      // Ensure a brush instance exists before setting props
      if (!canvas.freeDrawingBrush) {
        try {
          canvas.freeDrawingBrush = new PencilBrush(canvas);
        } catch (e) {
          console.error('Failed to initialize drawing brush', e);
        }
      }
      if (canvas.freeDrawingBrush) {
        canvas.freeDrawingBrush.width = Math.max(1, Number(brushSize) || 1);
        canvas.freeDrawingBrush.color = strokeColor || theme.palette.common.black;
      }
    } else {
      canvas.isDrawingMode = false;
      canvas.selection = false;
    }
  }, [canvas, selectedTool, brushSize, strokeColor]);

  // Context menu for canvas (right-click)
  const [canvasMenu, setCanvasMenu] = useState(null); // { mouseX, mouseY } | null
  useEffect(() => {
    if (!canvas || !canvas.upperCanvasEl) return;
    const handler = (e) => {
      e.preventDefault();
      setCanvasMenu({ mouseX: e.clientX - 2, mouseY: e.clientY - 4 });
    };
    canvas.upperCanvasEl.addEventListener('contextmenu', handler);
    return () => {
      canvas.upperCanvasEl?.removeEventListener('contextmenu', handler);
    };
  }, [canvas]);

  const closeCanvasMenu = () => setCanvasMenu(null);
  const handleCanvasDelete = () => {
    if (!canvas) return closeCanvasMenu();
    const target = canvas.getActiveObject();
    if (target) {
      canvas.remove(target);
      canvas.requestRenderAll();
      safeUpdateLayersFromCanvas();
    }
    closeCanvasMenu();
  };

  const handleToolClick = (toolId) => {
    setSelectedTool(toolId);
    if (!canvas) return;
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    if (toolId === 'text') {
      addText(canvas, { text: 'Click to edit', left: cx, top: cy, fontFamily, fontSize, fill: fillColor });
    } else if (toolId === 'rectangle') {
      addRect(canvas, { left: cx - 50, top: cy - 50, width: 100, height: 100, fill: fillColor, stroke: strokeColor, strokeWidth });
    } else if (toolId === 'circle') {
      addCircle(canvas, { left: cx - 50, top: cy - 50, radius: 50, fill: fillColor, stroke: strokeColor, strokeWidth });
    } else if (toolId === 'line') {
      addLine(canvas, { points: [50, 50, 200, 200], stroke: strokeColor, strokeWidth });
    }
  };

  // Context menu actions
  const moveLayerUp = (layerId) => {
    if (!canvas) return;
    const layer = layers.find((l) => l.id === layerId);
    if (layer?.object?.bringForward) {
      layer.object.bringForward();
      canvas.renderAll();
      updateLayersFromCanvas();
    }
  };
  const moveLayerDown = (layerId) => {
    if (!canvas) return;
    const layer = layers.find((l) => l.id === layerId);
    if (layer?.object?.sendBackwards) {
      layer.object.sendBackwards();
      canvas.renderAll();
      updateLayersFromCanvas();
    }
  };

  const renameLayer = (layerId) => {
    if (!canvas) return;
    const layer = layers.find((l) => l.id === layerId);
    const obj = layer?.object;
    if (!obj) return;
    const current = typeof obj.name === 'string' ? obj.name : (obj.type || 'Layer');
    const next = window.prompt('Rename layer', current);
    if (!next || next === current) return;
    obj.name = next;
    canvas.renderAll();
    updateLayersFromCanvas();
  };

  const safeUpdateLayersFromCanvas = useCallback(() => {
    if (isLayersDragging.current) {
      console.log('[PhotoAds] updateLayersFromCanvas suppressed during DnD');
      return;
    }
    updateLayersFromCanvas();
  }, [updateLayersFromCanvas]);

  // Upload a PNG thumbnail for a template asset (256px width target)
  const uploadTemplateThumbnail = useCallback(async (assetId) => {
    try {
      if (!canvas || !assetId) return;
      const cw = typeof canvas.getWidth === 'function' ? canvas.getWidth() : (canvas.width || 1);
      const targetW = 256;
      const multiplier = Math.max(0.1, Math.min(4, targetW / cw));
      const dataUrl = canvas.toDataURL({ format: 'png', multiplier });
      await api.post(`/photo-ads/templates/${assetId}/thumbnail`, { dataUrl });
    } catch (err) {
      console.warn('Thumbnail upload failed (non-fatal):', err && (err.message || err));
    }
  }, [canvas]);

  const handleCanvasReady = useCallback((c) => {
    if (!c) return;
    // avoid resetting same canvas instance or re-attaching handlers
    if (c.__photoAdsInit) return;
    c.__photoAdsInit = true;
    setCanvas(c);
    c.on('selection:created', safeUpdateLayersFromCanvas);
    c.on('selection:updated', safeUpdateLayersFromCanvas);
    c.on('selection:cleared', safeUpdateLayersFromCanvas);
    c.on('object:added', safeUpdateLayersFromCanvas);
    c.on('object:removed', safeUpdateLayersFromCanvas);
    c.on('object:modified', safeUpdateLayersFromCanvas);
    // Record initial state
    try { recordHistory(c); } catch (_) {}
    // Record on mutations (debounced)
    c.on('object:added', () => recordHistoryDebounced(c));
    c.on('object:removed', () => recordHistoryDebounced(c));
    c.on('object:modified', () => recordHistoryDebounced(c));
    c.on('path:created', () => recordHistoryDebounced(c));
    c.on('mouse:up', () => recordHistoryDebounced(c));
    // Apply current viewport zoom to wrapper so initial view matches UI state
    try {
      if (typeof c.setZoom === 'function') c.setZoom(1);
      const host = transformRef.current || c.wrapperEl || c.upperCanvasEl?.parentElement || c.lowerCanvasEl?.parentElement;
      if (host && host.style) {
        host.style.transformOrigin = 'center center';
        host.style.transform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;
      }
    } catch (_) {}
    safeUpdateLayersFromCanvas();
  }, [setCanvas, safeUpdateLayersFromCanvas, zoom, pan]);

  const applyFilter = (filterId) => {
    if (!canvas) return;
    const obj = canvas.getActiveObject();
    if (!obj || obj.type !== 'image') return;
    obj.filters = [];
    if (filterId && filterId !== 'none') {
      switch (filterId) {
        case 'sepia':
          obj.filters.push(new fabricFilters.Sepia());
          break;
        case 'grayscale':
          obj.filters.push(new fabricFilters.Grayscale());
          break;
        case 'vintage':
          obj.filters.push(new fabricFilters.Vintage());
          break;
        case 'pixelate':
          obj.filters.push(new fabricFilters.Pixelate({ blocksize: 4 }));
          break;
        case 'blur':
          obj.filters.push(new fabricFilters.Blur({ blur: 0.5 }));
          break;
        case 'sharpen':
          obj.filters.push(new fabricFilters.Convolute({ matrix: [0, -1, 0, -1, 5, -1, 0, -1, 0] }));
          break;
      }
      // adjustments
      obj.filters.push(
        new fabricFilters.Brightness({ brightness: (brightness - 100) / 100 }),
        new fabricFilters.Contrast({ contrast: (contrast - 100) / 100 }),
        new fabricFilters.Saturation({ saturation: (saturation - 100) / 100 })
      );
    }
    obj.applyFilters();
    canvas.renderAll();
    setSelectedFilter(filterId);
    recordHistoryDebounced(canvas);
  };

  // Layer helpers
  const selectLayer = (layer) => { canvas?.setActiveObject(layer.object); canvas?.renderAll(); setSelectedLayer(layer.object); };
  const toggleLayerVisibility = (layer) => { layer.object.visible = !layer.object.visible; canvas?.renderAll(); safeUpdateLayersFromCanvas(); };
  const toggleLayerLock = (layer) => { layer.object.selectable = !layer.object.selectable; layer.object.evented = !layer.object.evented; canvas?.renderAll(); safeUpdateLayersFromCanvas(); };
  const deleteSelected = (layer) => {
    const target = layer?.object || canvas?.getActiveObject();
    if (target && canvas) { canvas.remove(target); canvas.renderAll(); safeUpdateLayersFromCanvas(); }
  };

  // Add new layer via LayersPanel (+)
  const addNewLayer = () => {
    if (!canvas) return;
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const obj = addRect(canvas, { left: cx - 50, top: cy - 50, width: 100, height: 100, fill: fillColor, stroke: strokeColor, strokeWidth });
    if (obj && !obj.name) obj.name = `Layer ${canvas.getObjects().length}`;
    updateLayersFromCanvas();
  };

  // Add Layer menu (standard + template placeholders)
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const openAddMenu = (anchorEl) => setAddMenuAnchor(anchorEl);
  const closeAddMenu = () => setAddMenuAnchor(null);
  const addTemplateText = (placeholder) => {
    if (!canvas) return;
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const obj = addText(canvas, { text: placeholder, left: cx, top: cy, fontFamily, fontSize, fill: fillColor });
    obj.name = placeholder;
    updateLayersFromCanvas();
    closeAddMenu();
  };
  const addTemplateImageBox = (placeholder) => {
    if (!canvas) return;
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const obj = addRect(canvas, { left: cx - 100, top: cy - 75, width: 200, height: 150, fill: theme.palette.action.hover, stroke: theme.palette.divider, strokeWidth: 1 });
    obj.name = placeholder;
    updateLayersFromCanvas();
    closeAddMenu();
  };

  const addTemplateQRCode = (placeholder) => {
    if (!canvas) return;
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    // Create a square placeholder for QR code
    const obj = addRect(canvas, { 
      left: cx - 75, 
      top: cy - 75, 
      width: 150, 
      height: 150, 
      fill: theme.palette.background.default, 
      stroke: theme.palette.primary.main, 
      strokeWidth: 2,
      strokeDashArray: [5, 5]
    });
    obj.name = placeholder;
    // Add QR code identifier for template processing
    obj.qrCodePlaceholder = true;
    updateLayersFromCanvas();
    closeAddMenu();
  };

  // Reorder layers from LayersPanel DnD (layers array is top-first)
  const reorderLayers = (orderedTopFirst) => {
    if (!canvas || !Array.isArray(orderedTopFirst)) return;
    // Optimistic UI update so dnd-kit doesn't snap back before Fabric updates
    setLayers(orderedTopFirst);
    const bottomFirst = [...orderedTopFirst].reverse();
    console.log('[PhotoAds] Reorder apply (top->bottom ids):', orderedTopFirst.map(l => l.id));
    // First attempt: use moveTo for deterministic stacking
    bottomFirst.forEach((layer, idx) => {
      if (layer?.object?.moveTo) layer.object.moveTo(idx);
    });
    canvas.renderAll();

    // Verify order
    const afterMoveIdsTopFirst = canvas.getObjects().map(o => o.__layerId || o.id).reverse();
    const desiredTopFirstIds = orderedTopFirst.map(l => l.id);
    const matches = afterMoveIdsTopFirst.length === desiredTopFirstIds.length && afterMoveIdsTopFirst.every((id, i) => id === desiredTopFirstIds[i]);
    if (!matches) {
      console.warn('[PhotoAds] moveTo order mismatch; applying hard reorder by remove/add', { afterMoveIdsTopFirst, desiredTopFirstIds });
      // Fallback: remove all objects and re-add in bottom-first order
      const objs = canvas.getObjects().slice();
      objs.forEach(obj => canvas.remove(obj));
      bottomFirst.forEach((layer) => { if (layer?.object) canvas.add(layer.object); });
      canvas.renderAll();
      const afterHardIdsTopFirst = canvas.getObjects().map(o => o.__layerId || o.id).reverse();
      console.log('[PhotoAds] After hard reorder (top->bottom):', afterHardIdsTopFirst);
    } else {
      console.log('[PhotoAds] moveTo order verified (top->bottom):', afterMoveIdsTopFirst);
    }
    // Reconcile with actual canvas order
    // If a drag is ongoing, reconciliation will run on drag end
    if (!isLayersDragging.current) {
      updateLayersFromCanvas();
    } else {
      console.log('[PhotoAds] Reconcile deferred until DnD end');
    }
  };

  // File menu actions
  const newProject = () => {
    if (!canvas) return;
    canvas.discardActiveObject();
    canvas.getObjects().forEach(obj => canvas.remove(obj));
    canvas.backgroundColor = theme.palette.common.white;
    canvas.renderAll();
    applyViewportTransform(zoom, pan);
    try { recordHistory(canvas); } catch (_) {}
    updateLayersFromCanvas();
    setCurrentAsset(null);
  };

  const printCanvas = () => {
    if (!canvas) return;
    try {
      const dataUrl = canvas.toDataURL({ format: 'png' });
      const w = window.open('', '_blank', 'width=800,height=600');
      if (!w) {
        // Popup blocked - fallback to direct print
        alert('Popup blocked. Please allow popups for this site or use Ctrl+P to print after downloading the image.');
        // Trigger download as fallback
        const link = document.createElement('a');
        link.download = 'photo-ad.png';
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
      w.document.write(`
        <html>
          <head>
            <title>Print Photo Ad</title>
            <style>
              body { margin: 0; padding: 20px; text-align: center; }
              img { max-width: 100%; height: auto; }
              @media print { 
                body { margin: 0; padding: 0; }
                img { width: 100%; height: auto; }
              }
            </style>
          </head>
          <body>
            <img src="${dataUrl}" onload="setTimeout(() => { window.print(); }, 100);" onerror="alert('Failed to load image for printing');" />
          </body>
        </html>
      `);
      w.document.close();
      
      // Close window after print dialog (with delay for user interaction)
      setTimeout(() => {
        try { w.close(); } catch (e) {}
      }, 5000);
    } catch (error) {
      console.error('Print failed:', error);
      alert('Print failed. Please try downloading the image instead.');
    }
  };

  // Page settings modal
  const [pageOpen, setPageOpen] = useState(false);
  const [pageInit, setPageInit] = useState({ widthPx: 1080, heightPx: 1080 });
  const [openDialog, setOpenDialog] = useState(false);
  const [openLoading, setOpenLoading] = useState(false);
  const [loadInvOpen, setLoadInvOpen] = useState(false);
  const [creatingAd, setCreatingAd] = useState(false);
  const openPageSettings = () => {
    if (!canvas) return setPageOpen(true);
    setPageInit({ widthPx: canvas.getWidth(), heightPx: canvas.getHeight() });
    setPageOpen(true);
  };
  const applyPageSettings = ({ widthPx, heightPx }) => {
    if (!canvas) return setPageOpen(false);
    const w = Math.max(100, Math.round(widthPx));
    const h = Math.max(100, Math.round(heightPx));
    canvas.setWidth(w);
    canvas.setHeight(h);
    canvas.renderAll();
    // Sync border to new size if present
    try { addOrUpdateBorder(borderOptions, { silentCreate: true }); } catch (_) {}
    setPageOpen(false);
  };

  // Open dialog handlers
  const onOpenMenu = () => setOpenDialog(true);
  const handleOpenClose = () => setOpenDialog(false);

  // Open a template asset for editing
  const handleOpenTemplateAsset = async (asset) => {
    if (!canvas || !asset || openLoading) return;
    setOpenLoading(true);
    try {
      // Fetch template JSON via helper (ensures correct parsing)
      const json = await loadTemplateAssetJson(asset.id);
      // Resize canvas to template's stored dimensions if present
      const tw = Number(asset.width);
      const th = Number(asset.height);
      if (tw > 0 && th > 0) {
        canvas.setWidth(Math.round(tw));
        canvas.setHeight(Math.round(th));
      }
      await applyTemplateJson(canvas, json);
      safeUpdateLayersFromCanvas();
      setOpenDialog(false);
      // Mark current asset as template so Save updates the template
      setCurrentAsset({ id: asset.id, name: asset.name, mimeType: asset.mimeType });
    } catch (e) {
      console.error('Failed to open template', e);
      alert(e?.message || 'Failed to open template');
    } finally {
      setOpenLoading(false);
    }
  };
  const handleOpenAsset = async (asset) => {
    if (!canvas || !asset || openLoading) return;
    setOpenLoading(true);
    try {
      const res = await api.get(`/photo-ads/assets/${asset.id}/data`, { responseType: 'blob' });
      const blob = res.data;
      const mime = asset.mimeType || blob.type;
      if (mime && mime.includes('application/vnd.dymen.photoads+json')) {
        const text = await blob.text();
        let json;
        try { json = JSON.parse(text); } catch (err) { throw new Error('Invalid project JSON'); }

        // Normalize JSON: drop stale blob: image srcs to avoid fabric error
        const dropped = { count: 0 };
        const scrubImage = (obj) => {
          if (!obj) return obj;
          if (obj.type === 'image' && typeof obj.src === 'string') {
            if (obj.src.startsWith('blob:')) {
              // cannot reload expired blob; drop this layer
              dropped.count += 1;
              return null;
            }
            // Encourage CORS-friendly loading
            obj.crossOrigin = obj.crossOrigin || 'anonymous';
          }
          return obj;
        };
        try {
          if (Array.isArray(json.objects)) {
            json.objects = json.objects.map(scrubImage).filter(Boolean);
          }
          if (json.backgroundImage && typeof json.backgroundImage === 'object') {
            const bg = scrubImage(json.backgroundImage);
            json.backgroundImage = bg || null;
          }
        } catch (_) {}

        // Resize canvas to the project's stored dimensions if provided
        const pw = Number(asset.width);
        const ph = Number(asset.height);
        if (pw > 0 && ph > 0) {
          canvas.setWidth(Math.round(pw));
          canvas.setHeight(Math.round(ph));
        }
        // Clear and load full project to preserve remaining layers
        canvas.clear();
        await new Promise((resolve) => canvas.loadFromJSON(json, () => { canvas.renderAll(); resolve(); }));
        safeUpdateLayersFromCanvas();
        if (dropped.count > 0) {
          alert(`${dropped.count} image layer(s) could not be restored due to expired blob URLs. Please re-import those images and re-save.`);
        }
        setOpenDialog(false);
        setCurrentAsset({ id: asset.id, name: asset.name, mimeType: asset.mimeType });
      } else {
        throw new Error('Selected asset is a flattened image, not a project. Please save and open a Project to preserve layers.');
      }
    } catch (e) {
      console.error('Failed to open asset', e);
      alert(e?.message || 'Failed to open saved file');
    } finally {
      setOpenLoading(false);
    }
  };

  const handleApplyTemplate = ({ widthPx, heightPx }) => {
    if (!canvas) return;
    const w = Math.max(100, Math.round(widthPx));
    const h = Math.max(100, Math.round(heightPx));
    canvas.setWidth(w);
    canvas.setHeight(h);
    canvas.renderAll();
    setOpenDialog(false);
  };

  // Import PNG as new layer
  const handleImportPng = () => {
    if (!canvas) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png';
    input.onchange = async (e) => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const url = URL.createObjectURL(file);
      try {
        await addImageLayerFromUrl(canvas, url, { center: true, scaleToFit: true });
        safeUpdateLayersFromCanvas?.();
      } catch (err) {
        console.error('Failed to import PNG', err);
        alert('Failed to import PNG');
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    input.click();
  };

  const handleSave = async () => {
    if (!canvas) return;
    const locationId = selectedLocation?.id || selectedLocation?._id;
    if (!locationId) {
      alert('No active location selected. Please select a location first.');
      return;
    }
    try {
      if (currentAsset?.id) {
        // Update existing: decide between Project JSON vs Template JSON by MIME
        const isTemplate = (currentAsset?.mimeType || '').includes('application/vnd.dymen.photoads.template+json');
        if (isTemplate) {
          await updateTemplateJsonAsset(canvas, { assetId: currentAsset.id, name: currentAsset.name });
          await uploadTemplateThumbnail(currentAsset.id);
          alert('Template updated');
        } else {
          await updateProjectJsonAsset(canvas, { assetId: currentAsset.id, name: currentAsset.name });
          alert('Project updated');
        }
      } else {
        // First save (create new)
        const name = window.prompt('Enter a name for this project');
        if (!name) return;
        const created = await saveProjectJsonToLocation(canvas, { locationId, name });
        setCurrentAsset({ id: created?.id, name: created?.name, mimeType: created?.mimeType });
        alert('Project saved');
      }
      // Also keep local project state in browser
      saveProject(canvas);
    } catch (e) {
      console.error(e);
      alert('Failed to save project');
    }
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: theme.palette.background.default }}>
      <TopBar currentAsset={currentAsset} onUndo={undo} onRedo={redo} onNew={newProject} onOpen={onOpenMenu} onEditTemplate={onOpenMenu} onImportPng={handleImportPng} onSave={handleSave} onDownload={() => downloadImage(canvas)} onPrint={printCanvas} onPageSettings={openPageSettings} onLoadFromInventory={onLoadFromInventory} onSaveAsTemplate={handleSaveAsTemplate} />
      <Box ref={containerRef} sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left sidebar: tools + border + shapes */}
        <Box sx={{ display: 'flex', flexDirection: 'column', width: 200, height: '100%', flexShrink: 0, overflow: 'hidden' }}>
          <ToolsPanel onToolClick={handleToolClick} />
          <ShapesPanel onAddShape={handleAddShape} />
        </Box>
        {/* Center area: top options bar + canvas */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
          <Box sx={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, px: 1, bgcolor: theme.palette.background.paper, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <IconButton size="small" onClick={undo} title="Undo" sx={{ color: theme.palette.text.primary }}>
              <UndoIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={redo} title="Redo" sx={{ color: theme.palette.text.primary }}>
              <RedoIcon fontSize="small" />
            </IconButton>
            <Divider orientation="vertical" flexItem sx={{ borderColor: theme.palette.divider }} />
            <IconButton size="small" onClick={togglePanMode} title={panMode ? 'Exit Pan' : 'Pan'} sx={{ color: panMode ? theme.palette.primary.main : theme.palette.text.primary }}>
              <PanToolIcon fontSize="small" />
            </IconButton>
            <Divider orientation="vertical" flexItem sx={{ borderColor: theme.palette.divider }} />
            <IconButton size="small" onClick={zoomOut} title="Zoom out" sx={{ color: theme.palette.text.primary }}>
              <ZoomOut fontSize="small" />
            </IconButton>
            <Typography variant="body2" sx={{ color: theme.palette.text.primary, minWidth: 48, textAlign: 'center' }}>{Math.round(zoom * 100)}%</Typography>
            <IconButton size="small" onClick={zoomIn} title="Zoom in" sx={{ color: theme.palette.text.primary }}>
              <ZoomIn fontSize="small" />
            </IconButton>
            <Divider orientation="vertical" flexItem sx={{ borderColor: theme.palette.divider }} />
            <IconButton size="small" onClick={fitToScreen} title="Fit to Screen" sx={{ color: theme.palette.text.primary }}>
              <FitScreenIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={resetZoomActual} title="Actual Size (100%)" sx={{ color: theme.palette.text.primary }}>
              <ActualSizeIcon fontSize="small" />
            </IconButton>
            <Divider orientation="vertical" flexItem sx={{ borderColor: theme.palette.divider }} />
            <Button size="small" variant="outlined" onClick={openBorderPopover} sx={{ color: theme.palette.text.primary, borderColor: theme.palette.divider, textTransform: 'none', height: 28, lineHeight: '28px' }}>
              Border
            </Button>
          </Box>
          <Popover
            open={Boolean(borderAnchorEl)}
            anchorEl={borderAnchorEl}
            onClose={closeBorderPopover}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{ sx: { bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, mt: 1 } }}
          >
            <Box sx={{ p: 1, width: 280, maxHeight: '60vh', overflowY: 'auto' }}>
              <BorderPanel
                options={borderOptions}
                onChange={handleBorderChange}
                onApplyPreset={(preset) => { handleBorderPreset(preset); }}
                onApply={(opts) => { handleBorderApply(opts); closeBorderPopover(); }}
              />
            </Box>
          </Popover>
          <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
            <CanvasStage
              outerRefExternal={outerViewportRef}
              transformRefExternal={transformRef}
              panMode={panMode}
              onPanMouseDown={onPanMouseDown}
              onPanMouseMove={onPanMouseMove}
              onPanMouseUp={endPan}
              canvasRefExternal={canvasRefExternal}
              onReady={handleCanvasReady}
            />
          </Box>
        </Box>
        {/* Vertical resizer between canvas and right panel */}
        <Box
          onMouseDown={onResizerMouseDown}
          sx={{ width: 6, cursor: 'col-resize', bgcolor: isResizing ? theme.palette.action.selected : theme.palette.divider, '&:hover': { bgcolor: theme.palette.action.hover } }}
        />
        <Paper sx={{ width: rightWidth, bgcolor: theme.palette.background.paper, borderRadius: 0, borderLeft: `1px solid ${theme.palette.divider}`, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <LayersPanel
            layers={layers}
            selectedLayer={selectedLayer}
            onSelect={selectLayer}
            onToggleLock={toggleLayerLock}
            onToggleVisible={toggleLayerVisibility}
            onDelete={deleteSelected}
            onAdd={openAddMenu}
            onReorder={reorderLayers}
            onContextMoveUp={moveLayerUp}
            onContextMoveDown={moveLayerDown}
            onContextRename={renameLayer}
            onDndStart={() => { isLayersDragging.current = true; console.log('[PhotoAds] DnD start'); }}
            onDndEnd={() => { isLayersDragging.current = false; console.log('[PhotoAds] DnD end; reconciling from canvas'); setTimeout(() => updateLayersFromCanvas(), 0); }}
          />
          <Menu anchorEl={addMenuAnchor} open={!!addMenuAnchor} onClose={closeAddMenu}>
            <MenuItem onClick={() => { addText(canvas, { text: 'Click to edit', left: (canvas?.width||0)/2, top: (canvas?.height||0)/2, fontFamily, fontSize, fill: fillColor }); updateLayersFromCanvas(); closeAddMenu(); }}>Text</MenuItem>
            <MenuItem onClick={() => { addRect(canvas, { left: (canvas?.width||0)/2 - 50, top: (canvas?.height||0)/2 - 50, width: 100, height: 100, fill: fillColor, stroke: strokeColor, strokeWidth }); updateLayersFromCanvas(); closeAddMenu(); }}>Rectangle</MenuItem>
            <MenuItem onClick={() => { addCircle(canvas, { left: (canvas?.width||0)/2 - 50, top: (canvas?.height||0)/2 - 50, radius: 50, fill: fillColor, stroke: strokeColor, strokeWidth }); updateLayersFromCanvas(); closeAddMenu(); }}>Circle</MenuItem>
            <MenuItem onClick={() => { addLine(canvas, { points: [50, 50, 200, 200], stroke: strokeColor, strokeWidth }); updateLayersFromCanvas(); closeAddMenu(); }}>Line</MenuItem>
            <Divider />
            <MenuItem disabled>Template placeholders</MenuItem>
            <MenuItem onClick={() => addTemplateText('{{TITLE}}')}>TITLE (text)</MenuItem>
            <MenuItem onClick={() => addTemplateText('{{PRICE}}')}>PRICE (text)</MenuItem>
            <MenuItem onClick={() => addTemplateText('{{SKU}}')}>SKU (text)</MenuItem>
            <MenuItem onClick={() => addTemplateText('{{DESCRIPTION}}')}>DESCRIPTION (text)</MenuItem>
            <Divider />
            <MenuItem onClick={() => addTemplateImageBox('{{IMAGE1}}')}>IMAGE1 (image)</MenuItem>
            <MenuItem onClick={() => addTemplateImageBox('{{IMAGE2}}')}>IMAGE2 (image)</MenuItem>
            <MenuItem onClick={() => addTemplateImageBox('{{IMAGE3}}')}>IMAGE3 (image)</MenuItem>
            <MenuItem onClick={() => addTemplateImageBox('{{IMAGE4}}')}>IMAGE4 (image)</MenuItem>
            <MenuItem onClick={() => addTemplateQRCode('{{QR_CODE}}')}>QR CODE (product link)</MenuItem>
            <MenuItem onClick={() => addTemplateImageBox('{{IMAGE5}}')}>IMAGE5 (image)</MenuItem>
          </Menu>
          <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, flex: 1, minHeight: 0 }}>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ '& .MuiTabs-scrollButtons': { color: theme.palette.text.primary } }}
            >
              <Tab label="Properties" sx={{ color: theme.palette.text.primary, minWidth: 'auto' }} />
              <Tab label="Blend" sx={{ color: theme.palette.text.primary, minWidth: 'auto' }} />
              <Tab label="Filters" sx={{ color: theme.palette.text.primary, minWidth: 'auto' }} />
              <Tab label="Textures" sx={{ color: theme.palette.text.primary, minWidth: 'auto' }} />
              <Tab label="Alignment" sx={{ color: theme.palette.text.primary, minWidth: 'auto' }} />
            </Tabs>
            <TabPanel value={activeTab} index={0}>
              <PropertiesPanel />
            </TabPanel>
            <TabPanel value={activeTab} index={1}>
              <BlendPanel />
            </TabPanel>
            <TabPanel value={activeTab} index={2}>
              <FiltersPanel selectedFilter={selectedFilter} onApply={applyFilter} />
            </TabPanel>
            <TabPanel value={activeTab} index={3}>
              <TexturesPanel />
            </TabPanel>
            <TabPanel value={activeTab} index={4}>
              <AlignmentPanel />
            </TabPanel>
          </Box>
        </Paper>
      </Box>
      <PageSettingsDialog open={pageOpen} initial={pageInit} onClose={() => setPageOpen(false)} onApply={applyPageSettings} />
      {/* Canvas context menu */}
      <Menu
        open={!!canvasMenu}
        onClose={closeCanvasMenu}
        anchorReference="anchorPosition"
        anchorPosition={canvasMenu ? { top: canvasMenu.mouseY, left: canvasMenu.mouseX } : undefined}
      >
        <MenuItem onClick={handleCanvasDelete} disabled={!canvas || !canvas.getActiveObject?.()}>Delete</MenuItem>
      </Menu>
      <OpenDialog
        open={openDialog}
        onClose={handleOpenClose}
        locationId={selectedLocation?.id || selectedLocation?._id}
        onOpenAsset={handleOpenAsset}
        onOpenTemplateAsset={handleOpenTemplateAsset}
        onApplyTemplate={handleApplyTemplate}
        loadingOpen={openLoading}
      />
      <LoadFromInventoryDialog open={loadInvOpen} onClose={handleLoadInvClose} locationId={selectedLocation?.id || selectedLocation?._id} onCreateAd={handleCreateAdFromInventory} creating={creatingAd} />
    </Box>
  );
};

export default PhotoAdsIndex;
